<template>
  <div class="page-data-analysis" v-loading="loading">
    <portal to="layout-nav-title">业务员部门数据统计</portal>

    <CustomerStructuresMenu />

    <h2 class="title" style="margin-top: 0">{{ title }}</h2>
    <el-form inline>
      <el-form-item label="报告类型">
        <PeriodSelect :period.sync="form.period" :date.sync="form.date" />
      </el-form-item>
      <el-form-item>
        <DialogLoading
          :visible="exportLoading"
          description="正在生成导出文件，预计需要 1-3 分钟，请等待"
          :title="`导出处理中`"
        />
        <el-button type="primary" @click="handleExport">导出</el-button>
      </el-form-item>
    </el-form>

    <el-tabs v-if="isMobile" v-model="activeTab">
      <el-tab-pane
        :label="table.title"
        :name="table.key"
        v-for="table in tableList"
        :key="table.key"
        :style="`--table-primary-color: ${table.color}`"
      >
        <el-tooltip effect="dark" placement="right" v-if="table.desc">
          <template #content>
            <div class="tooltip-content">
              <p>{{ table.desc }}</p>
            </div>
          </template>
          <el-button type="text" class="help-icon">
            表格说明
            <i class="el-icon-question"></i>
          </el-button>
        </el-tooltip>
        <RankTable
          :data="table.data"
          sortable-field="count"
          :is-percent="table.isPercent"
        />
      </el-tab-pane>
    </el-tabs>

    <template v-else>
      <div
        v-for="table in tableList"
        :key="table.title"
        class="table-group"
        :style="`--table-primary-color: ${table.color}`"
      >
        <div class="group-title">
          {{ table.title }}
          <el-tooltip effect="dark" placement="right" v-if="table.desc">
            <template #content>
              <div class="tooltip-content">
                <p>{{ table.desc }}</p>
              </div>
            </template>
            <el-button type="text" class="help-icon">
              <i class="el-icon-question"></i>
            </el-button>
          </el-tooltip>
        </div>
        <RankTable
          :data="table.data"
          sortable-field="count"
          horizontal
          :is-percent="table.isPercent"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { Vue, Component, Mixins, Watch } from 'vue-property-decorator';
import Utils from '@/components/utils.vue';
import RankTable from './rank-table.vue';
import moment from 'moment';
import _ from 'lodash';
import { screenService } from 'beans-admin-plugin';
import { colors } from './colors';
import PeriodSelect from '@/components/period-select.vue';
import CustomerStructuresMenu from './menu.vue';
import DialogLoading from '@/components/dialog-loading.vue';
import { exportFile } from '@/utils/export';

@Component({
  components: {
    RankTable,
    PeriodSelect,
    CustomerStructuresMenu,
    DialogLoading,
  },
})
export default class DataAnalysis extends Mixins(Vue, Utils) {
  exportLoading = false;
  data = {};
  activeTab = 'by_new_customer_count';
  form = {
    period: 'month',
    date: moment().startOf('month').format('YYYY-MM-DD'),
  };

  get date() {
    return this.form.date;
  }

  get title() {
    if (this.form.period === 'year') {
      return `${moment(this.date).format('YYYY年')}业务员部门数据统计`;
    }
    if (this.form.period === 'month') {
      return `${moment(this.date).format('YYYY年MM月')}业务员部门数据统计`;
    }
    const today = moment();
    const endWeek = moment(this.date).endOf('isoWeek');
    const endDate = moment.min(endWeek, today);
    const format = 'YYYY年MM月DD日';
    return `${moment(this.date)
      .startOf('isoWeek')
      .format(format)} ~ ${endDate.format(format)}业务员部门数据统计`;
  }

  get isMobile() {
    return screenService.isMobile;
  }

  get tableListAllGroup() {
    return [
      {
        key: 'by_new_customer_count',
        countKey: 'new_customer_count',
        title: '新客数',
      },
      {
        key: 'by_first_ordered_ratio',
        countKey: 'first_ordered_ratio',
        title: '新客转化率',
        isPercent: true,
        desc: '时间范围内首单的新客户 / 时间范围内总的新客户数',
      },
      { key: 'by_order_amount', countKey: 'order_amount', title: '总成交额' },
      {
        key: 'by_ordered_customer_count',
        countKey: 'ordered_customer_count',
        title: '月总成交客户',
      },
      {
        key: 'by_total_customer_count',
        countKey: 'customer_count_at_the_end',
        title: '总客户数',
      },
      {
        key: 'by_followed_customer_count',
        countKey: 'followed_customers_count',
        title: '客户触达',
      },
      {
        key: 'by_followed_ratio',
        countKey: 'followed_ratio',
        title: '触达率',
        isPercent: true,
        desc: '时间范围内触达的客户 / 总客户数',
      },
    ].map((group) => {
      return {
        ...group,
        color: colors[group.key],
        data: _.get(this.data, group.key, []).map((item) => {
          return {
            ...item,
            name: item.user_screen_name,
            count:
              typeof item[group.countKey] === 'string'
                ? Number(item[group.countKey])
                : item[group.countKey],
          };
        }),
      };
    });
  }

  get tableList() {
    return this.tableListAllGroup;
  }

  loading = false;
  async fetchData() {
    try {
      this.loading = true;
      const data = await this.$request.get(
        '/reports/customer_structures/ranked_users',
        { ...this.form }
      );
      this.data = data;
    } finally {
      this.loading = false;
    }
  }

  validPeriods = ['week', 'month', 'year'];

  created() {
    const query = this.$route.query;
    Object.assign(
      this.form,
      _.pickBy(
        query,
        (value, key) => value && Object.keys(this.form).includes(key)
      )
    );
    if (!this.validPeriods.includes(this.form.period)) {
      this.form.period = 'month';
      this.form.date = moment().startOf('month').format('YYYY-MM-DD');
    }
    this.debounceFetchData = _.debounce(this.fetchData, 500);
  }

  @Watch('form.period')
  @Watch('form.date')
  async handleChangeForm() {
    this.$router.replace({ query: { ...this.$route.query, ...this.form } }).catch(() => {});
  }

  @Watch('$route.query')
  async handleChangeQuery() {
    this.debounceFetchData();
  }

  mounted() {
    this.fetchData();
  }

  async handleExport() {
    try {
      this.exportLoading = true;
      await exportFile('reports/customer_structures/ranked_users', {
        ...this.form,
        return_xlsx: true,
      });
    } finally {
      this.exportLoading = false;
    }
  }
}

export const route = [
  {
    path: '/reports/customer_structures/ranked_users',
    name: 'reports/customer_structures.ranked_users',
    component: DataAnalysis,
    meta: {
      can: [],
    },
  },
];
</script>

<style lang="scss" scoped>
.page-data-analysis {
  padding: 20px;
}

.table-group {
  margin-bottom: 20px;

  .table-horizontal {
    /deep/ .el-table__header {
      thead {
        tr {
          th:first-child {
            background-color: var(--table-primary-color);
          }
        }
      }
    }

    /deep/ .el-table__row {
      td:first-child {
        background-color: var(--table-primary-color);
      }
    }
  }
}
</style>
